"use client";

import React from "react";
import { Clock, ChevronRight, TrendingUp } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
function AdPlaceholder({
  type
}: {
  type: "in-feed" | "sidebar";
}) {
  if (type === "sidebar") {
    return <div className="w-full rounded-2xl overflow-hidden border border-border/50 shadow-md bg-white">
        <a href="https://pdaaustralia.com.au/product/australian-parents-compass-pda/" style={{
        display: 'block'
      }}>
          <img src="https://pdaaustralia.com.au/wp-content/uploads/2026/03/parents-compass-cover.jpg" alt="The Australian Parent's Compass" style={{
          width: '100%',
          height: 'auto',
          objectFit: 'contain',
          display: 'block',
          background: '#fff'
        }} />
        </a>
        <div style={{
        padding: '1.25rem'
      }}>
          <span style={{
          display: 'inline-block',
          background: '#15803d',
          color: '#fff',
          fontSize: '.7rem',
          fontWeight: 700,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          padding: '.2rem .65rem',
          borderRadius: '3px',
          marginBottom: '.6rem'
        }}>Parents</span>
          <p style={{
          margin: '0 0 .4rem',
          fontSize: '1rem',
          fontWeight: 700,
          lineHeight: 1.3,
          color: '#111'
        }}>The Australian Parent's Compass</p>
          <p style={{
          margin: '0 0 .9rem',
          fontSize: '.875rem',
          color: '#4b5563',
          lineHeight: 1.5
        }}>Navigating PDA with connection and clarity — the complete guide for exhausted Australian families.</p>
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '.75rem'
        }}>
            <span style={{
            fontSize: '1.4rem',
            fontWeight: 800,
            color: '#111'
          }}>$37</span>
          </div>
          <a href="https://pdaaustralia.com.au/product/australian-parents-compass-pda/" style={{
          display: 'block',
          background: '#e63329',
          color: '#fff',
          padding: '.55rem 1rem',
          borderRadius: '5px',
          textDecoration: 'none',
          fontWeight: 700,
          fontSize: '.9rem',
          textAlign: 'center'
        }}>Buy Now →</a>
        </div>
      </div>;
  }
  return <div className="my-12 flex h-[120px] md:h-[250px] w-full flex-col items-center justify-center bg-secondary/20 border border-secondary/30 text-muted-foreground text-xs tracking-widest uppercase rounded-xl">
      <span>Community Notice</span>
      <span className="mt-2 opacity-50">Latest Australian Updates</span>
    </div>;
}
function ArticleSkeleton() {
  return <div className="flex flex-col md:flex-row gap-6 py-8 border-b border-border animate-pulse">
      <div className="w-full md:w-1/3 aspect-[3/2] bg-muted rounded-xl" />
      <div className="flex-1 flex flex-col justify-center space-y-4">
        <div className="flex gap-2">
          <div className="h-4 w-16 bg-muted rounded-full" />
          <div className="h-4 w-24 bg-muted rounded-full" />
        </div>
        <div className="h-6 w-3/4 bg-muted rounded-lg" />
        <div className="h-4 w-full bg-muted rounded-lg" />
        <div className="h-4 w-2/3 bg-muted rounded-lg" />
      </div>
    </div>;
}
function PopularPostsWidget() {
  const [topPosts, setTopPosts] = React.useState<Array<{
    id: number;
    title: string;
    link: string;
    category: string;
  }>>([]);
  React.useEffect(() => {
    fetch('https://pdaaustralia.com.au/wp-json/wp/v2/posts?per_page=4&orderby=date&order=desc&_fields=id,title,link,categories,_links&_embed').then(r => r.json()).then(posts => {
      const mapped = posts.map((p: any) => ({
        id: p.id,
        title: p.title?.rendered?.replace(/&#8217;/g, "'").replace(/&#8211;/g, "–").replace(/&amp;/g, "&") || '',
        link: p.link || '/articles/',
        category: p._embedded?.['wp:term']?.[0]?.[0]?.name || 'Article'
      }));
      setTopPosts(mapped);
    }).catch(() => {});
  }, []);
  if (!topPosts.length) return null;
  return <div className="mb-12">
      <div className="flex items-center gap-2 mb-6">
        <TrendingUp className="size-5 text-primary" />
        <h3 className="text-lg font-serif font-bold tracking-tight text-primary">Essential Reading</h3>
      </div>
      <div className="space-y-6">
        {topPosts.map((post, i) => <div key={post.id} data-index={i} className="group flex gap-4 items-start">
            <span className="text-2xl font-serif font-bold text-secondary group-hover:text-accent transition-colors">
              0{i + 1}
            </span>
            <div>
              <span className="text-xs font-sans font-semibold text-accent uppercase tracking-wider mb-1 block">
                {post.category}
              </span>
              <Link to={post.link} className="text-sm font-sans font-medium leading-tight text-foreground hover:text-accent transition-colors line-clamp-2">
                {post.title}
              </Link>
            </div>
          </div>)}
      </div>
    </div>;
}
function ArchiveConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    refetch
  } = usePosts();
  if (error) {
    return <div className="py-24 text-center">
        <p className="text-destructive font-sans mb-4">We encountered an error loading the articles.</p>
        <Button onClick={refetch} variant="outline" className="rounded-full border-primary text-primary hover:bg-primary hover:text-white">Try Again</Button>
      </div>;
  }
  return <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 items-start">
      <div className="lg:col-span-3">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-serif font-bold tracking-tight mb-4 text-primary">
            PDA Articles, Stories and Strategies
          </h1>
          <p className="text-lg md:text-xl font-sans text-muted-foreground max-w-2xl leading-relaxed">
            Explore our comprehensive collection of evidence-based articles, lived-experience stories, and practical strategies designed to support the PDA community in Australia.
          </p>
        </div>

        <Separator className="mb-8 bg-border/50" />

        <div className="flex flex-col">
          {loading ? Array.from({
          length: 5
        }).map((_, i) => <div key={i} data-index={i}>
                <ArticleSkeleton />
              </div>) : posts.length === 0 ? <div className="py-12 text-center text-muted-foreground font-sans">
              No articles found in the hub at this time.
            </div> : posts.map((post, i) => <React.Fragment key={post.id}>
                <article data-index={i} className="group flex flex-col md:flex-row gap-6 md:gap-10 py-10 border-b border-border/40 first:pt-0">
                  <Link to={post.link || "#"} className="w-full md:w-1/3 shrink-0 overflow-hidden rounded-xl bg-muted aspect-[3/2] relative shadow-sm">
                    {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-secondary/10">
                        PDA Australia
                      </div>}
                  </Link>

                  <div className="flex-1 flex flex-col justify-center">
                    <div className="flex items-center gap-3 text-xs font-sans text-muted-foreground mb-3">
                      {post.categories && post.categories.length > 0 && <span className="font-semibold text-accent uppercase tracking-widest">
                          {post.categories[0].name}
                        </span>}
                      {post.date && <span className="flex items-center gap-1 opacity-80">
                          <Clock className="size-3" />
                          {new Date(post.date).toLocaleDateString('en-AU', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                        </span>}
                    </div>
                    
                    <h2 className="text-2xl md:text-3xl font-serif font-bold leading-tight mb-4 text-primary group-hover:text-accent transition-colors duration-300">
                      <Link to={post.link || "#"}>
                        {post.title}
                      </Link>
                    </h2>
                    
                    {post.excerpt && <p className="text-muted-foreground font-sans text-lg leading-relaxed line-clamp-2 mb-6" dangerouslySetInnerHTML={{
                __html: post.excerpt
              }} />}

                    <div className="mt-auto">
                      <Link to={post.link || "#"} className="inline-flex items-center text-base font-semibold text-accent hover:text-primary transition-colors group/link">
                        Read Full Article <ChevronRight className="ml-1 size-4 transition-transform group-hover/link:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                </article>

                {(i + 1) % 3 === 0 && i !== posts.length - 1 && <AdPlaceholder type="in-feed" />}
              </React.Fragment>)}
        </div>

        {!loading && posts.length > 0 && <div className="mt-12 pt-8 border-t border-border/50">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious onClick={prevPage} aria-disabled={!hasPrev} className={!hasPrev ? "pointer-events-none opacity-30" : "cursor-pointer rounded-full hover:bg-secondary/20 font-sans"} />
                </PaginationItem>
                <PaginationItem>
                  <span className="text-sm font-semibold text-muted-foreground px-4 font-sans">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext onClick={nextPage} aria-disabled={!hasNext} className={!hasNext ? "pointer-events-none opacity-30" : "cursor-pointer rounded-full hover:bg-secondary/20 font-sans"} />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>}
      </div>

      <aside className="lg:col-span-1 hidden lg:block">
        <div className="sticky top-32">
          <PopularPostsWidget />

          <div className="mt-8 w-full rounded-2xl overflow-hidden border border-border/50 shadow-md bg-white">
            <a href="https://pdaaustralia.com.au/product/australian-parents-compass-pda/" style={{
            display: 'block'
          }}>
              <img src="https://pdaaustralia.com.au/wp-content/uploads/2026/03/parents-compass-cover.jpg" alt="The Australian Parent's Compass" style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
              background: '#fff'
            }} />
            </a>
            <div style={{
            padding: '1.25rem'
          }}>
              <span style={{
              display: 'inline-block',
              background: '#15803d',
              color: '#fff',
              fontSize: '.7rem',
              fontWeight: 700,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              padding: '.2rem .65rem',
              borderRadius: '3px',
              marginBottom: '.6rem'
            }}>Parents</span>
              <p style={{
              margin: '0 0 .4rem',
              fontSize: '1rem',
              fontWeight: 700,
              lineHeight: 1.3,
              color: '#111'
            }}>The Australian Parent's Compass</p>
              <p style={{
              margin: '0 0 .9rem',
              fontSize: '.875rem',
              color: '#4b5563',
              lineHeight: 1.5
            }}>Navigating PDA with connection and clarity — the complete guide for exhausted Australian families.</p>
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '.75rem'
            }}>
                <span style={{
                fontSize: '1.4rem',
                fontWeight: 800,
                color: '#111'
              }}>$37</span>
              </div>
              <a href="https://pdaaustralia.com.au/product/australian-parents-compass-pda/" style={{
              display: 'block',
              background: '#e63329',
              color: '#fff',
              padding: '.55rem 1rem',
              borderRadius: '5px',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '.9rem',
              textAlign: 'center'
            }}>Buy Now →</a>
            </div>
          </div>

          <div className="mt-8 w-full rounded-2xl overflow-hidden border border-border/50 shadow-md bg-white">
            <a href="https://pdaaustralia.com.au/product/funded-ndis-guide-pda/" style={{
            display: 'block'
          }}>
              <img src="https://pdaaustralia.com.au/wp-content/uploads/2026/03/funded-cover.jpg" alt="FUNDED — The Complete NDIS Guide for PDA Families" style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
              background: '#fff'
            }} />
            </a>
            <div style={{
            padding: '1.25rem'
          }}>
              <span style={{
              display: 'inline-block',
              background: '#1d4ed8',
              color: '#fff',
              fontSize: '.7rem',
              fontWeight: 700,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              padding: '.2rem .65rem',
              borderRadius: '3px',
              marginBottom: '.6rem'
            }}>NDIS</span>
              <p style={{
              margin: '0 0 .4rem',
              fontSize: '1rem',
              fontWeight: 700,
              lineHeight: 1.3,
              color: '#111'
            }}>FUNDED — The Complete NDIS Guide for PDA Families</p>
              <p style={{
              margin: '0 0 .9rem',
              fontSize: '.875rem',
              color: '#4b5563',
              lineHeight: 1.5
            }}>Every template you need to secure, build, and protect your child's NDIS funding in 2026.</p>
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '.75rem'
            }}>
                <span style={{
                textDecoration: 'line-through',
                color: '#9ca3af',
                fontSize: '.85em',
                fontWeight: 400,
                marginRight: '.4rem'
              }}>$97</span>
                <span style={{
                fontSize: '1.4rem',
                fontWeight: 800,
                color: '#e63329'
              }}>$67</span>
                <span style={{
                display: 'inline-block',
                background: '#e63329',
                color: '#fff',
                fontSize: '.65em',
                fontWeight: 700,
                padding: '.15rem .5rem',
                borderRadius: '4px'
              }}>SALE</span>
              </div>
              <a href="https://pdaaustralia.com.au/product/funded-ndis-guide-pda/" style={{
              display: 'block',
              background: '#e63329',
              color: '#fff',
              padding: '.55rem 1rem',
              borderRadius: '5px',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '.9rem',
              textAlign: 'center'
            }}>Buy Now →</a>
            </div>
          </div>

        </div>
      </aside>
    </div>;
}
export default function FullArchive() {
  const wp_query_params = {
    per_page: 12,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="educational-articles" className="w-full bg-background py-16 md:py-32">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <PostsProvider wp_query={{
        per_page: 12,
        order: "desc",
        orderby: "date"
      }}>
          <ArchiveConsumer />
        </PostsProvider>
      </div>
    </section>;
}